// Team → provider profile, services and availability (routes/staff.ts).
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';

async function authHeaders(json = true): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');
  return { Authorization: `Bearer ${session.access_token}`, ...(json ? { 'Content-Type': 'application/json' } : {}) };
}

export async function staffApi<T = any>(path: string, init: { method?: string; body?: any; form?: FormData } = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/staff${path}`, {
    method: init.method ?? 'GET',
    headers: await authHeaders(!init.form),
    body: init.form ?? (init.body !== undefined ? JSON.stringify(init.body) : undefined),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw Object.assign(new Error(json.error || 'Something went wrong'), { code: json.code, details: json.details });
  return json.data as T;
}

export interface Certification { name: string; issuer?: string | null; year?: number | null }

export interface StaffProfile {
  mappingId: string;
  userId: string;
  name: string;
  role: string;
  isProvider: boolean;
  photoUrl: string | null;
  coverUrl: string | null;
  headline: string;
  bio: string;
  specialties: string[];
  certifications: Certification[];
  experienceYears: number | null;
  languages: string[];
  gender: string | null;
  levelId: string | null;
  isPublic: boolean;
  sortOrder: number;
  introVideoUrl: string;
}

export interface StaffLevel { id: string; name: string; rank: number; tone: string; role?: string; staffCount?: number }

export interface ProfileResponse {
  profile: StaffProfile;
  levels: StaffLevel[];
  suggestions: { specialties: string[]; languages: string[] };
  settings: { selfEdit: boolean; showGender: boolean };
  can: { editProfile: boolean; editLevel: boolean; editVisibility: boolean };
}

export interface CoachPreview {
  id: string;
  name: string;
  role: string;
  photoUrl: string | null;
  headline: string | null;
  specialties: string[];
  experienceYears: number | null;
  languages: string[];
  level: { name: string; tone: string } | null;
  nextFree: { date: string; startTime: string } | null;
}

export interface AvailabilityWindow { id?: string; dayOfWeek: number; startTime: string; endTime: string; classIds: string[] }
export interface AvailabilityBlock { id: string; date: string; startTime: string; endTime: string; kind: string; reason: string | null; branchId: string | null }
export interface AvailabilityLeave { id: string; leaveType: string; startDate: string; endDate: string; isHalfDay: boolean; halfDayType: string | null; status: string }

export interface AvailabilityResponse {
  branches: Array<{ id: string; name: string }>;
  branchId: string | null;
  weekStart: string;
  day: { start: string; end: string };
  windows: AvailabilityWindow[];
  blocks: AvailabilityBlock[];
  leaves: AvailabilityLeave[];
  bookables: Array<{ id: string; name: string; bookableType: string }>;
}

export interface ServicesResponse {
  level: { id: string; name: string } | null;
  bookables: Array<{ id: string; name: string; bookableType: string; scheduleMode: string; delivers: boolean; openToRole: boolean }>;
  offerings: Array<{
    ownerType: 'plan' | 'service';
    id: string;
    name: string;
    basePrice: number;
    levelPrice: number | null;
    override: number | null;
    effective: { price: number; source: 'staff' | 'level' | 'base' };
  }>;
}

export const PROVIDER_ROLES = ['trainer', 'therapist', 'consultant'];

/** Level badge tone → the admin palette (tokens live in the member apps; the admin uses Tailwind). */
export const TONE_CLASS: Record<string, string> = {
  ink: 'bg-zinc-900 text-white',
  accent: 'bg-lime-400 text-zinc-900',
  info: 'bg-sky-100 text-sky-800',
  warn: 'bg-amber-100 text-amber-800',
  mint: 'bg-emerald-100 text-emerald-800',
  muted: 'bg-zinc-100 text-zinc-600',
};

export const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
